import type { ApiResponse, ApiError } from "@/lib/api";
import type {
  HotelListApiResponse,
  HotelDetailApiResponse,
  HotelsByDestinationResponse,
} from "@/types/hotel";
import type { FilterLookups } from "@/types/hotel-filters";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "https://www.lilylanka.com";

export interface HotelSearchParams {
  status?: number;
  location?: string;
  minStars?: number;
  search?: string;
  amenities?: string;
  sortby?: string;
  sortorder?: string;
  minAmount?: number;
  maxAmount?: number;
  propertyTypes?: string;
  page?: number;
  pageSize?: number;
}

export interface HotelDetailParams {
  checkIn?: string;
  checkOut?: string;
  adults?: number;
  children?: number;
}

class HotelService {
  async searchHotels(
    params: HotelSearchParams,
  ): Promise<ApiResponse<HotelListApiResponse>> {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append("status", (params?.status || 2).toString());
      queryParams.append("page", (params?.page || 1).toString());
      queryParams.append("pageSize", (params?.pageSize || 20).toString());

      if (params?.location) {
        queryParams.append("location", params.location);
      }
      if (params?.search) {
        queryParams.append("search", params.search);
      }
      if (params?.minStars) {
        queryParams.append("minStars", params.minStars.toString());
      }
      if (params?.amenities) {
        queryParams.append("amenities", params.amenities);
      }
      if (params?.minAmount !== undefined && params.minAmount > 0) {
        queryParams.append("minamount", params.minAmount.toString());
      }
      if (params?.maxAmount !== undefined && params.maxAmount > 0) {
        queryParams.append("maxamount", params.maxAmount.toString());
      }
      if (params?.propertyTypes) {
        queryParams.append("propertytypes", params.propertyTypes);
      }
      if (params?.sortby) {
        queryParams.append("sortby", params.sortby);
      }
      if (params?.sortorder) {
        queryParams.append("sortorder", params.sortorder);
      }

      const url = `${API_BASE_URL}/api/Hotels?${queryParams.toString()}`;

      console.log("🔍 Fetching hotels from:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
      });

      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          data: data as HotelListApiResponse,
          status: response.status,
        };
      } else {
        let errorData: ApiError = {
          title: response.statusText,
          status: response.status,
        };
        try {
          errorData = await response.json();
        } catch {}
        return { success: false, data: errorData, status: response.status };
      }
    } catch (error) {
      return {
        success: false,
        data: {
          title: "Network Error",
          detail: error instanceof Error ? error.message : "Unknown error",
        },
        status: 0,
      };
    }
  }

  async getHotelDetail(
    id: string | number,
    params?: HotelDetailParams,
  ): Promise<ApiResponse<HotelDetailApiResponse>> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.checkIn) {
        const checkInDate = new Date(params.checkIn);
        if (!isNaN(checkInDate.getTime())) {
          queryParams.append(
            "checkIn.year",
            checkInDate.getFullYear().toString(),
          );
          queryParams.append(
            "checkIn.month",
            (checkInDate.getMonth() + 1).toString(),
          );
          queryParams.append("checkIn.day", checkInDate.getDate().toString());
          queryParams.append(
            "checkIn.dayOfWeek",
            checkInDate.getDay().toString(),
          );
        }
      }

      if (params?.checkOut) {
        const checkOutDate = new Date(params.checkOut);
        if (!isNaN(checkOutDate.getTime())) {
          queryParams.append(
            "checkOut.year",
            checkOutDate.getFullYear().toString(),
          );
          queryParams.append(
            "checkOut.month",
            (checkOutDate.getMonth() + 1).toString(),
          );
          queryParams.append("checkOut.day", checkOutDate.getDate().toString());
          queryParams.append(
            "checkOut.dayOfWeek",
            checkOutDate.getDay().toString(),
          );
        }
      }

      if (params?.adults !== undefined) {
        queryParams.append("adults", params.adults.toString());
      }
      if (params?.children !== undefined) {
        queryParams.append("children", params.children.toString());
      }

      const url = `${API_BASE_URL}/api/Hotels/${id}${queryParams.toString() ? "?" + queryParams.toString() : ""}`;

      console.log("🔍 Fetching hotel detail from:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
      });

      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          data: data as HotelDetailApiResponse,
          status: response.status,
        };
      } else {
        let errorData: ApiError = {
          title: response.statusText,
          status: response.status,
        };
        try {
          errorData = await response.json();
        } catch {}
        return { success: false, data: errorData, status: response.status };
      }
    } catch (error) {
      return {
        success: false,
        data: {
          title: "Network Error",
          detail: error instanceof Error ? error.message : "Unknown error",
        },
        status: 0,
      };
    }
  }

  async getHotelsByDestination(
    destinationId: number,
    params?: { page?: number; pageSize?: number },
  ): Promise<ApiResponse<HotelsByDestinationResponse>> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", String(params?.page || 1));
      queryParams.append("pageSize", String(params?.pageSize || 20));

      const url = `${API_BASE_URL}/api/CustomerDestinations/${destinationId}/hotels?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
      });

      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          data: data as HotelsByDestinationResponse,
          status: response.status,
        };
      } else {
        let errorData: ApiError = {
          title: response.statusText,
          status: response.status,
        };
        try {
          errorData = await response.json();
        } catch {}
        return { success: false, data: errorData, status: response.status };
      }
    } catch (error) {
      return {
        success: false,
        data: {
          title: "Network Error",
          detail: error instanceof Error ? error.message : "Unknown error",
        },
        status: 0,
      };
    }
  }

  async getFilterLookups(token: string): Promise<FilterLookups | null> {
    try {
      const url = `${API_BASE_URL}/api/lookup`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        return {
          starRatings: data.starRatings || [],
          categories: data.categories || [],
          accessModes: data.accessModes || [],
          paymentMethods: data.paymentMethods || [],
          payoutMethods: data.payoutMethods || [],
          amenities: data.amenities || [],
        };
      }
      return null;
    } catch (error) {
      console.error("Failed to fetch lookups:", error);
      return null;
    }
  }

  async getRooms(hotelId: number, token: string) {
    try {
      const url = `${API_BASE_URL}/api/Hotels/${hotelId}/rooms`;
      console.log("🔍 Fetching rooms from:", url);

      const headers: HeadersInit = {
        Accept: "application/json",
      };

      if (token && token.trim().length > 0) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers,
        cache: "no-store",
      });

      if (response.ok) {
        const data = await response.json();
        return { success: true, data, status: response.status };
      } else {
        let errorData: ApiError = {
          title: response.statusText,
          status: response.status,
        };
        try {
          errorData = await response.json();
        } catch {}
        return { success: false, data: errorData, status: response.status };
      }
    } catch (error) {
      return {
        success: false,
        data: {
          title: "Network Error",
          detail: error instanceof Error ? error.message : "Unknown error",
        },
        status: 0,
      };
    }
  }

  async getLocation(locationId: number, token: string) {
    try {
      const url = `${API_BASE_URL}/api/Location/${locationId}`;
      console.log("🔍 Fetching location from:", url);

      const headers: HeadersInit = {
        Accept: "application/json",
      };

      if (token && token.trim().length > 0) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers,
        cache: "no-store",
      });

      if (response.ok) {
        const data = await response.json();
        return { success: true, data, status: response.status };
      } else {
        let errorData: ApiError = {
          title: response.statusText,
          status: response.status,
        };
        try {
          errorData = await response.json();
        } catch {}
        return { success: false, data: errorData, status: response.status };
      }
    } catch (error) {
      return {
        success: false,
        data: {
          title: "Network Error",
          detail: error instanceof Error ? error.message : "Unknown error",
        },
        status: 0,
      };
    }
  }
}

export const hotelService = new HotelService();
